import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import { Scale } from 'webapp/enums/enums';
import { equal } from 'webapp/utils/common';
import SheetSelection from 'webapp/utils/sheet-selection';
import type { ValidationRule } from 'webapp/utils/validate';

import type CustomMetricSetupFormPrebuilt from 'webapp/models-custom/custom-metric-setup-form/prebuilt';
import type CustomMetricSetupFormSheet from 'webapp/models-custom/custom-metric-setup-form/sheet';
import Validator from 'webapp/models-custom/validator';

import type { RSjqXHR, RSPromise } from 'webapp/services/request';

type LabelValuePairArray = LabelValuePairString[] | LabelValuePairNumeric[]; // numeric only for scale
export interface NameIdPair {
	id: string;
	name: string;
}
type MetricDefinitionValue = SomeDataObject;
export type ValueFormatted =
	boolean | string | number | NameIdPair | NameIdPair[] | MetricDefinitionValue;
export type ValueSelected = boolean | string | number | string[] | MetricDefinitionValue;

export default class CustomMetricSetupFormItemField extends Validator {
	@service('constants') declare constantsService: Service.Constants;
	@service('request') declare requestService: Service.Request;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;

	id: string;
	name: string;
	type: string;
	description: string;
	required: boolean;
	sameRow: boolean;
	depends: string;
	dependsShow: string[];
	dependsHide: string[];
	hidden: boolean;
	definesRawId: boolean;
	method: string;
	validationMethod: string;
	maxItems: number;
	helpbox: string;
	size: number;
	minValue: number | null;
	maxValue: number | null;
	parameters: Record<string, string> | null;
	private rawOptions: APIData.CustomQuery.Prebuilt.BuilderItemOption[];

	form: CustomMetricSetupFormPrebuilt | CustomMetricSetupFormSheet;

	// Set when setInitialSelected() got a saved id no longer in the option list, so the UI
	// can flag it. The value is preserved either way.
	@tracked hasInvalidSavedValue = false;

	@tracked private _value: ValueSelected | null = null;
	get value(): ValueSelected | null {
		return this._value;
	}

	set value(value: ValueSelected | null) {
		this._value = value;
		this.form.bumpValueChangeCounter();
	}

	constructor(
		fieldData: APIData.CustomQuery.Prebuilt.BuilderItem,
		form: CustomMetricSetupFormPrebuilt,
	) {
		super();

		this.form = form;

		this.id = fieldData.id;
		this.name = fieldData.name;
		this.type = fieldData.type;
		this.description = fieldData.description || '';
		this.required = !!fieldData.required;
		this.sameRow = !!fieldData.sameRow;
		this.depends = fieldData.depends || '';
		this.dependsShow = fieldData.dependsShow || [];
		this.dependsHide = fieldData.dependsHide || [];
		this.hidden = !!fieldData.hidden;
		this.definesRawId = !!fieldData.definesRawId;
		this.method = fieldData.method || '';
		this.validationMethod = fieldData.validationMethod || '';
		this.maxItems = fieldData.maxItems || Infinity;
		this.helpbox = fieldData.helpbox || '';
		this.size = fieldData.size || 2;
		this.minValue = fieldData.minValue || null;
		this.maxValue = fieldData.maxValue || null;
		this.parameters = fieldData.parameters || null;
		this.value = (fieldData.value as ValueSelected) || null;

		// for fields, the "items" are always options (f.ex in dropdown)
		this.rawOptions =
			(fieldData.items as APIData.CustomQuery.Prebuilt.BuilderItemOption[]) || [];

		// have helpbox opened only if user has created 3 or less sql custom metrics
		const currentUserId = this.currentUserService.numericId;
		this.isHelpboxOpen =
			this.mmCustomQueriesService.customQueryModels.filter((customQuery) => {
				return (
					customQuery.isSQL &&
					!customQuery.isSheets &&
					customQuery.createdById === currentUserId
				);
			}).length <= 3;
	}

	// -------- field types and logic ---------

	get isMultiSelect() {
		return this.type === 'multiselect';
	}

	get isMultiCombo() {
		return this.type === 'multicombo';
	}

	get isDate() {
		return this.type === 'date';
	}

	get isDatetime() {
		return this.type === 'datetime';
	}

	get isTime() {
		return this.type === 'time';
	}

	get isSQL() {
		return this.type === 'sql';
	}

	get isJSON() {
		return this.type === 'json';
	}

	get isTextarea() {
		return this.type === 'textarea';
	}

	get isTimezone() {
		return this.type === 'timezone';
	}

	get isCheckbox() {
		return this.type === 'checkbox';
	}

	get isRadio() {
		return this.type === 'radio';
	}

	get isNumber() {
		return this.type === 'number';
	}

	get isFormat() {
		return this.type === 'format';
	}

	get isScale() {
		return this.type === 'scale';
	}

	get isA1() {
		return this.type === 'a1';
	}

	get isMetricDefinition() {
		return this.type === 'metricDefinition';
	}

	get isMetricInvert() {
		return this.type === 'metricInvert';
	}

	get isSelect(): boolean {
		return this.isSingleSelect || this.isMultiSelect;
	}

	get isSingleSelect() {
		return this.type === 'select' || this.isTimezone || this.isScale;
	}

	get isCombo() {
		return this.type === 'combo' || this.isMultiCombo || this.isFormat;
	}

	get isDatepicker() {
		return this.isDate || this.isDatetime || this.isTime;
	}

	get isCodeEditor(): boolean {
		return this.isSQL || this.isJSON;
	}

	// -------- value & formatting ---------

	@tracked valueFormatted: ValueFormatted | null = null;
	@tracked private defaultValueFormatted: ValueFormatted | null = null;
	@tracked private initiallySelectedOptions: LabelValuePairArray = [];

	get hasValue(): boolean {
		return !!this.value || this.value === 0;
	}

	get isDefault(): boolean {
		// for advanced autoopen
		return equal(this.valueFormatted, this.defaultValueFormatted);
	}

	// object that will be merged into settings sent to BE
	get valueRecord(): APIData.CustomQuery.Prebuilt.SettingsData {
		return {
			[this.id]: this.valueFormatted,
		};
	}

	get isValueNumeric(): boolean {
		return this.isNumber || this.isScale;
	}

	get isSingleValue(): boolean {
		return !this.isMultiSelect && !this.isMultiCombo;
	}

	private get initialOptionsForDropdown(): LabelValuePairString[] {
		return this.rawOptions.map((item: SomeDataObject) => {
			const description = item.description;
			return {
				value: String(item.id),
				label: item.name,
				label3:
					description &&
					`<ui-icon name="InfoFill" size="sm" title="${description}"></ui-icon>`,
			};
		});
	}

	private get isValueFormattedIdAndNameObject(): boolean {
		return (this.isCombo || this.isSelect || this.isRadio) && !this.isScale && !this.isFormat;
	}

	private isValueFormattedNameIdPair(_value: ValueFormatted): _value is NameIdPair {
		return this.isValueFormattedIdAndNameObject && this.isSingleValue;
	}

	private isValueFormattedNameIdPairArray(_value: ValueFormatted): _value is NameIdPair[] {
		return this.isValueFormattedIdAndNameObject && !this.isSingleValue;
	}

	updateValueFormatted(optionValues: LabelValuePairArray = []) {
		this.valueFormatted = this.getValueFormatted(optionValues);
	}

	setInitialValues(settings: APIData.CustomQuery.Prebuilt.SettingsData): void {
		const optionValues = this.getOptionValues();
		this.defaultValueFormatted = this.getValueFormatted(optionValues);
		if (settings && (settings[this.id] || settings[this.id] === 0 || this.isCheckbox)) {
			this.setInitialSelected(settings[this.id] as ValueFormatted); // a field's own value
		} else {
			this.updateValueFormatted(optionValues);
		}

		if (this.required && !this.hasValue) {
			this.isValid = false;
		}
	}

	setInitialSelected(selected: ValueFormatted | null) {
		const optionValues = this.getOptionValues();
		// the selected value must be on the list (if list is given)
		const onlyListedAllowed = !!(optionValues.length && (this.isSelect || this.isRadio));

		let initiallySelectedOptions: LabelValuePairArray = [];
		if (this.isCheckbox && typeof selected === 'boolean') {
			this.value = selected;
		} else if (selected || selected === 0) {
			if (this.isValueFormattedIdAndNameObject) {
				// Always preserve saved id/name pairs (even when the id is no longer in the
				// option list, e.g. dataset schema drift) so the user can see and fix them.
				if (this.isValueFormattedNameIdPairArray(selected)) {
					selected.forEach((sel) => {
						const inOptions = !!(optionValues as LabelValuePairString[]).find(
							(item) => item.value === String(sel.id),
						);
						(initiallySelectedOptions as LabelValuePairString[]).push({
							value: String(sel.id),
							label: sel.name,
						});
						if (onlyListedAllowed && !inOptions) {
							this.hasInvalidSavedValue = true;
						}
					});
					this.value = initiallySelectedOptions.map((item) => item.value);
				} else if (this.isValueFormattedNameIdPair(selected)) {
					const inOptions = !!(optionValues as LabelValuePairString[]).find(
						(item) => item.value === selected.id,
					);
					initiallySelectedOptions = [
						{
							value: selected.id,
							label: selected.name,
						},
					];
					if (onlyListedAllowed && !inOptions) {
						this.hasInvalidSavedValue = true;
					}
					this.value = initiallySelectedOptions[0]
						? initiallySelectedOptions[0].value
						: null;
				}
				this.initiallySelectedOptions = initiallySelectedOptions;
			} else if (this.isScale) {
				// exception
				if (selected === Scale.AUTO_BE) {
					this.value = Scale.AUTO;
				} else {
					this.value = selected;
				}
			} else {
				// single input
				this.value = selected;
			}
		} else if (this.value) {
			// default value from manifest for certain fields (e.g. sheets "config")
			this.value = selected;
		} else {
			this.value = null;
		}
		this.updateValueFormatted(initiallySelectedOptions);
	}

	getOptionValues(): LabelValuePairArray {
		let optionValues: LabelValuePairArray = [];
		if (this.isTimezone) {
			optionValues = this.constantsService.timezones;
		} else if (this.isFormat) {
			optionValues = this.constantsService.formatOptions;
			// add custom format that is selected but not on the list
			if (!optionValues.find((item) => item.value === (this.value as string))) {
				const v = this.value as string;
				optionValues = Array.from(optionValues);
				optionValues.push({
					value: v,
					label: String(v),
				});
			}
		} else if (this.isScale) {
			optionValues = this.constantsService.scales;
		} else {
			const optionsForDropdown = this.initialOptionsForDropdown;
			optionValues = optionsForDropdown.length
				? optionsForDropdown
				: this.initiallySelectedOptions;
		}
		return optionValues;
	}

	private findOptionById(id: string | number, options: LabelValuePairArray) {
		if (!options?.length) {
			return null;
		}

		let opt = (options as SomeDataObject[]).find((item) => item.value === id);
		if (!opt) {
			const firstValue = options[0]!.value;
			if (typeof firstValue === 'string') {
				opt = (options as LabelValuePairString[]).find((item) => item.value === String(id));
			} else if (typeof firstValue === 'number') {
				opt = (options as LabelValuePairNumeric[]).find((opt) => id === +opt.value);
			}
		}

		return opt;
	}

	private getValueFormatted(optionValues: LabelValuePairArray) {
		let valueFormatted = null;

		const value = this.value;
		if (this.isMultiCombo || this.isMultiSelect) {
			valueFormatted = [];
		} else if (this.isCheckbox) {
			valueFormatted = !!value;
		}

		if (value || value === 0) {
			if (this.isValueFormattedIdAndNameObject) {
				if (this.isMultiCombo || this.isMultiSelect) {
					// multiple ids and names
					(value as string[]).forEach((v) => {
						const opt = this.findOptionById(v, optionValues);
						valueFormatted.push({
							id: v,
							name: opt ? opt.label : v,
						});
					});
				} else {
					// have id and name, but single item
					const opt = this.findOptionById(value as string, optionValues);
					valueFormatted = {
						id: value,
						name: opt ? opt.label : value,
					};
				}
			} else if (this.isScale) {
				// exception
				valueFormatted = value;
				if (value === Scale.AUTO) {
					valueFormatted = Scale.AUTO_BE;
				}
			} else {
				// single input
				valueFormatted = value;
				if (this.isValueNumeric) {
					valueFormatted = this.accountService.parseAmericanNumber(
						valueFormatted as number,
					);
				}
			}
		}
		return valueFormatted;
	}

	// ------ current field status & handlers ---------

	@tracked isHelpboxOpen = true;
	@tracked isFocused = false;

	@tracked private _isHidden = false;
	get isHidden(): boolean {
		return this._isHidden;
	}

	set isHidden(value: boolean) {
		this._isHidden = value;
		this.form.bumpHiddenChangeCounter();
	}

	get isHiddenOnUI(): boolean {
		return (
			this.hidden || this.isHidden || (this.id === '__type' && this.rawOptions?.length < 2)
		);
	}

	get sizeClass(): string {
		let size = String(this.size);
		if (this.isMetricDefinition) {
			size = '2-x3'; // size of three fields of size 2
		}

		if (this.isMetricInvert) {
			size = '100';
		}

		return `size-${size}`;
	}

	// -------- validation & error handling ---------

	@tracked isValid = true;
	@tracked isValidating = false;
	@tracked isValidatingWithRequest = false;
	@tracked validationWarning = '';

	validationPromise: Promise<unknown> | null = null;

	get isValidAndValidated(): boolean {
		return this.isValid && !this.isValidating;
	}

	override get validationRules(): Record<string, ValidationRule[]> {
		const valueRules: ValidationRule[] = [];

		if (
			this.required &&
			!this.isHiddenOnUI // make fields required if they are also visible on UI
		) {
			valueRules.push('required');
		}

		if (
			this.isNumber &&
			!this.isHiddenOnUI && // don't validate hidden number fields
			typeof this.minValue === 'number' &&
			typeof this.maxValue === 'number'
		) {
			valueRules.push({
				rule: 'numeric-between-values',
				opts: {
					minValue: this.minValue,
					maxValue: this.maxValue,
				},
			});
		}

		if (this.isA1) {
			valueRules.push({
				rule: 'custom-function',
				opts: {
					errText: 'This is not a valid A1 notation.',
					fn: (value) => {
						let isValid;

						if (value) {
							const selection = SheetSelection.fromA1(value as string);

							isValid = selection.isValid;

							if (isValid && selection.a1Prefix) {
								// validate that sheet name exists
								isValid = (
									this.form as CustomMetricSetupFormSheet
								).sheetNamesForValidation.some((sheetName) => {
									return "'" + sheetName + "'!" === selection.a1Prefix;
								});
							}
						} else {
							isValid = true;
						}

						return isValid;
					},
				},
			});
		}

		return { value: valueRules };
	}

	triggerValidate(): Promise<boolean> {
		this.isValidating = true;

		// just FE instant rules validation
		const isValid = this.validate();

		if (isValid && this.validationMethod && this.value) {
			// validating through BE endpoint

			// if request already in progress, cancel it
			const validationXHR = (this.validationPromise as RSPromise<unknown>)?.jqPromise as
				RSjqXHR | undefined;
			if (validationXHR && validationXHR.readyState !== 4) {
				validationXHR.abort();
			}

			const data: SomeDataObject = {
				method: this.validationMethod,
				parameters: {},
			};
			data.parameters[this.id] = this.valueFormatted;

			const promise = this.requestService.api<APIData.CustomQuery.FieldValidationResponse>(
				'POST',
				`${this.form.builderUrl}/validate`,
				data,
				{ timeout: 60 * 1000 },
			);

			this.validationPromise = promise;
			this.isValidatingWithRequest = true;
			this.validationWarning = '';
			this.form.checkResetErrors();

			return new Promise((resolve) => {
				promise
					.then(
						(reply) => {
							// test data to update other fields (gsheets example)
							// reply = {"updateFields": {"format": "EUR #,###", "queryName": "Revenue"}, "warnings": [{"title": "zomg validation error", "description": "zomg validation description", "link": "https://databox.com"}]};

							if (!reply) {
								return;
							}

							if (reply.updateFields && Object.keys(reply.updateFields).length) {
								// if extra fields are returned
								// f.ex. on gsheets when value range validation returns suggestion for "format" field
								this.form.externalFieldUpdate(reply.updateFields);
							}

							if (reply.warnings?.length) {
								const message = this.form.getFormattedError(reply.warnings);
								this.validationWarning = message;
							}
						},
						(errorData) => {
							// test data
							// errorData.errors = [{title: 'zomg validation error', description: 'zomg validation description', link: 'https://databox.com'}];

							if (!errorData.code) {
								return;
							}

							let message;
							try {
								message = this.form.getFormattedError(errorData.errors);
							} catch (_e) {
								message = `Invalid selected ${this.id}`;
							}

							this.setValidationErrors({
								value: [{ message }],
							});

							// throw errorData;
						},
					)
					.finally(() => {
						this.isValidating = false;
						this.isValidatingWithRequest = false;

						const isValid = this.errorsArray.length === 0;
						this.isValid = isValid;

						resolve(isValid);
					})
					.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
			});
		} else {
			this.isValidating = false;
			this.isValid = isValid;

			return Promise.resolve(isValid);
		}
	}
}
